export type ConsentCategories = {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  performance: boolean;
  advertisement: boolean;
};

export const DEFAULT_CONSENT: ConsentCategories = {
  necessary: true,
  functional: false,
  analytics: false,
  performance: false,
  advertisement: false
};

const GRANTED_CONSENT: ConsentCategories = {
  necessary: true,
  functional: true,
  analytics: true,
  performance: true,
  advertisement: true
};

/**
 * Set NEXT_PUBLIC_COOKIE_CONSENT_OVERRIDE=true to treat consent as fully
 * granted, bypassing CookieYes entirely. CookieYes is configured per-domain
 * in its own dashboard and silently no-ops (script loads, but
 * `window.getCkyConsent` never appears) on any domain that isn't registered
 * there — this unblocks analytics testing on UAT/preview without needing a
 * CookieYes registration for every such domain. Never set this in production.
 */
const consentOverrideEnabled =
  process.env.NEXT_PUBLIC_COOKIE_CONSENT_OVERRIDE === 'true';

declare global {
  interface Window {
    getCkyConsent?: () => {
      isUserActionCompleted: boolean;
      categories: ConsentCategories;
    };
  }
}

export function getCkyConsentCategories(): ConsentCategories {
  if (consentOverrideEnabled) return GRANTED_CONSENT;
  if (typeof window === 'undefined' || !window.getCkyConsent) {
    return DEFAULT_CONSENT;
  }
  return window.getCkyConsent().categories;
}

/** Full CookieYes state including whether the user has interacted with the banner. */
export function getCkyConsentState(): {
  isUserActionCompleted: boolean;
  categories: ConsentCategories;
} {
  if (consentOverrideEnabled) {
    return { isUserActionCompleted: true, categories: GRANTED_CONSENT };
  }
  if (typeof window === 'undefined' || !window.getCkyConsent) {
    return { isUserActionCompleted: false, categories: DEFAULT_CONSENT };
  }
  return window.getCkyConsent();
}
